import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    FolderSection,
    PlacementPayload,
    RevisionedCommandResponse,
    StudyEntityType,
} from '@faro/study-designer-model';
import { createSectionDuplicateMutation, SectionDuplicateHook } from '../createSectionDuplicateMutation';
import { createSectionInsertMutation, SectionInsertHook } from '../createSectionInsertMutation';
import { useFolderSectionDelete, useFolderSectionMove, useFolderSectionUpdate } from './FolderSectionHooks';

export type FolderSectionInsertHook = SectionInsertHook<Partial<FolderSection>>;

/**
 * Mutation hook to insert a folder section.
 * @returns A function that inserts a folder section.
 */
export const useFolderSectionInsertMutation: (studyId: string) => FolderSectionInsertHook = createSectionInsertMutation<
    Partial<FolderSection>
>(StudyEntityType.FolderSection, 'folder-section', 'Failed to create folder section');

export type FolderSectionUpdateHook = (
    folderSection: FolderSection
) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to update a folder section.
 * @returns A function that updates a folder section.
 */
export function useFolderSectionUpdateMutation(studyId: string): FolderSectionUpdateHook {
    const { showErrorHandler } = useAppContext();

    const updateFolderSection = useFolderSectionUpdate({ studyId });

    function update(folderSection: FolderSection) {
        return updateFolderSection
            .mutateAsync(folderSection)
            .catch(showErrorHandler('Failed to update folder section'));
    }

    return update;
}

export type FolderSectionDeleteHook = (folderSection: {
    id: string;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to delete a folder section.
 * @returns A function that deletes a folder section.
 */
export function useFolderSectionDeleteMutation(studyId: string): FolderSectionDeleteHook {
    const { showErrorHandler } = useAppContext();

    const deleteFolderSection = useFolderSectionDelete({ studyId });

    function deleteFolder(folderSection: { id: string }) {
        return deleteFolderSection
            .mutateAsync({
                id: folderSection.id,
                studyId,
            })
            .catch(showErrorHandler('Failed to delete folder section'));
    }

    return deleteFolder;
}

export type FolderSectionDuplicateHook = SectionDuplicateHook;

/**
 * Mutation hook to duplicate a folder section.
 * @returns A function that duplicates a folder section.
 */
export const useFolderSectionDuplicateMutation: (studyId: string) => FolderSectionDuplicateHook =
    createSectionDuplicateMutation(
        StudyEntityType.FolderSection,
        'folder-section',
        'Failed to duplicate folder section'
    );

export type FolderSectionMoveHook = (folderSection: {
    id: string;
    placement: PlacementPayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to move a folder section.
 * @returns A function that moves a folder section.
 */
export function useFolderSectionMoveMutation(studyId: string): FolderSectionMoveHook {
    const { showErrorHandler } = useAppContext();

    const moveFolderSection = useFolderSectionMove({ studyId });

    function move(folderSection: { id: string; placement: PlacementPayload }) {
        return moveFolderSection
            .mutateAsync({
                ...folderSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to move folder section'));
    }

    return move;
}
